/**
 * Journey / pre-shift helpers (pure). Face-to-face only in v1.
 */

import { DEFAULT_GEOFENCE_RADIUS_METERS, checkGeofence } from './geofence'
import {
  compactJourneyPings,
  JOURNEY_PING_MIN_INTERVAL_MS,
  MAX_STORED_JOURNEY_PINGS,
} from './journey-ping-policy'
import { haversineKm } from './km'
import { REMINDER_BEFORE_START_MS } from './reminders'
import type { ChecklistDiscipline, GeoPoint, JourneyPing, SessionJourney } from './types'

/**
 * Storage cap for en-route location pings — alias of
 * {@link MAX_STORED_JOURNEY_PINGS} (journey-ping-policy.ts), kept for callers
 * that predate the cadence/compaction overhaul. The origin ping is always
 * retained; beyond the cap the OLDER run is distance-compacted rather than
 * dropped (see `appendJourneyPing` in journey-ping-policy.ts), so the route
 * trace survives.
 *
 * History: 5 (one ping per stage) → 40 at a 60 s cadence (2026-10-06 field
 * test: stage-only pings left the member on a stale countdown) → 240 with
 * compaction at the 20 s / 150 m cadence.
 */
export const MAX_JOURNEY_PINGS = MAX_STORED_JOURNEY_PINGS

/**
 * Target cadence for provider position pings while en route (ms) — alias of
 * {@link JOURNEY_PING_MIN_INTERVAL_MS}; the full time/distance/accuracy rule
 * is `evaluateJourneyPingCadence` in journey-ping-policy.ts.
 */
export const JOURNEY_PING_INTERVAL_MS = JOURNEY_PING_MIN_INTERVAL_MS

/**
 * A GPS-sourced ping older than this is "stale": it still anchors the ETA
 * (with staleness decay) but no longer suppresses the time-based stage
 * fallback, and GPS-pair speed observation ignores pairs further apart than
 * this. Deliberately NOT tied to the ping cadence: it is the longest silence
 * (phone locked, Maps in front) across which assuming continuous travel is
 * still reasonable.
 */
export const JOURNEY_GPS_FRESH_MS = 3 * 60_000

/**
 * Six provider journey stages shown on the progress bar.
 *
 * Stages 1–5 are observational (time/GPS driven). Stage 5 — "Very close by" —
 * is the GPS geofence state: the provider's position is within
 * {@link DEFAULT_GEOFENCE_RADIUS_METERS} of the session address. Stage 6 —
 * "Arrived" — is set by GPS auto-arrival (a tight, accuracy-checked fix inside
 * {@link ARRIVAL_GEOFENCE_RADIUS_METERS} — see {@link shouldAutoArrive}) or by
 * check-in, which implies it. The explicit "I've arrived" button was removed
 * (owner decision 2026-10-07: automate arrival through GPS); the earlier
 * double-push problem is gone with the button, and the two signals now use
 * DIFFERENT radii so each fires at most once: "very close" at 500 m, "arrived"
 * at 150 m.
 */
export const JOURNEY_STAGE_COUNT = 6

export type JourneyStage = 1 | 2 | 3 | 4 | 5 | 6

/** GPS geofence proximity stage — the approach signal ("very close by"). */
export const JOURNEY_VERY_CLOSE_STAGE: JourneyStage = 5
/** Arrival stage — GPS auto-arrival ({@link shouldAutoArrive}) or check-in. */
export const JOURNEY_ARRIVED_STAGE: JourneyStage = 6

/**
 * Auto-arrival geofence (owner decision 2026-10-07, superseding the earlier
 * explicit-"I've arrived"-button design): arrival is GPS-automated. This
 * radius is deliberately TIGHTER than {@link DEFAULT_GEOFENCE_RADIUS_METERS}
 * (500 m), which remains the "very close by" approach signal — so the member
 * gets "very close" on geofence approach and "arrived" on actual arrival,
 * each at most once.
 */
export const ARRIVAL_GEOFENCE_RADIUS_METERS = 150

/**
 * A fix coarser than this must never auto-arrive. Lesson from the 97-hour
 * travel-estimate bug (2026-10-06): coarse network/IP geolocation can be
 * kilometres (or continents) off — only a genuinely tight GPS fix may declare
 * the provider "arrived" to the member.
 */
export const ARRIVAL_MAX_ACCURACY_METERS = 100

export interface AutoArriveInput {
  journey: SessionJourney | null | undefined
  destination: GeoPoint | null | undefined
  /** The incoming journey ping (must be a real GPS fix with accuracy). */
  ping: Pick<JourneyPing, 'lat' | 'lng' | 'accuracy' | 'locationSource' | 'isMock'>
}

/**
 * Whether an incoming journey ping should auto-record arrival (pure policy —
 * the session-journey lambda applies it server-side, so arrival fires exactly
 * once regardless of which device pinged):
 *
 *  - journey must be actively en route;
 *  - the fix must be real GPS (never an `estimate` ping, which clones stale
 *    coordinates, and never a mock/fake-location fix);
 *  - the fix must carry a reported accuracy ≤
 *    {@link ARRIVAL_MAX_ACCURACY_METERS} (no accuracy → no auto-arrival);
 *  - the fix must be within {@link ARRIVAL_GEOFENCE_RADIUS_METERS} of the
 *    session coordinates.
 *
 * Foreground-only GPS means this may never fire (phone locked, Maps in
 * front) — check-in from en_route is the fallback: it records arrival and
 * check-in together (see update-session-state).
 */
export function shouldAutoArrive(input: AutoArriveInput): boolean {
  const { journey, destination, ping } = input
  if (!journey || journey.status !== 'en_route' || !destination) return false
  if (ping.locationSource === 'estimate') return false
  if (ping.isMock) return false
  const accuracy = ping.accuracy
  if (accuracy == null || !Number.isFinite(accuracy) || accuracy < 0) return false
  if (accuracy > ARRIVAL_MAX_ACCURACY_METERS) return false
  return checkGeofence(ping, destination, ARRIVAL_GEOFENCE_RADIUS_METERS).withinRadius
}

export const JOURNEY_STAGE_LABELS: readonly [string, string, string, string, string, string] = [
  'Not left yet',
  'On their way',
  'Halfway',
  'Nearly there',
  'Very close by',
  'Arrived',
]

/** Minimum movement from the journey start ping to auto-advance to stage 2 (metres). */
export const JOURNEY_MOVEMENT_THRESHOLD_METERS = 50

/** Elapsed-travel fractions that trigger auto pings after journey start (T/5 … 4T/5). */
export const JOURNEY_PING_ELAPSED_FRACTIONS: readonly [number, number, number, number] = [
  1 / 5,
  2 / 5,
  3 / 5,
  4 / 5,
]

/** Route progress thresholds for stages 3 and 4 (0–1 toward destination). */
export const JOURNEY_HALFWAY_PROGRESS = 0.45
export const JOURNEY_NEARLY_THERE_PROGRESS = 0.85

/**
 * Bounds for trusting the observed average speed between GPS fixes (km/h).
 * Outside these, fall back to {@link URBAN_AVG_SPEED_KMH} — a crawling or
 * implausibly fast average would produce absurd ETAs.
 */
export const MIN_OBSERVED_SPEED_KMH = 5
export const MAX_OBSERVED_SPEED_KMH = 110

/** Observed speed needs at least this much data to be meaningful. */
const MIN_SPEED_OBSERVATION_MS = 2 * 60_000
const MIN_SPEED_OBSERVATION_KM = 0.8

/** Minutes before session start the provider should arrive (not just reach the property). */
export const EARLY_ARRIVAL_MINUTES = 5

/** Fixed cushion on top of drive time for parking / last-minute delays (minutes). */
export const BASE_TRAVEL_BUFFER_MINUTES = 2

/**
 * Extra leave-by safety margin (owner decision after the 2026-10-08 field
 * test, where the computed leave-by landed AFTER the provider had already
 * left and the drive still ran long). Applied to leave-by only — never to the
 * drive-time figure shown as "~N min travel" or used for the ETA.
 */
export const LEAVE_BY_BUFFER_MINUTES = 5

/**
 * Crow-flies distance is shorter than driven distance — multiply before speed calc.
 * Typical urban/suburban range is ~1.2–1.4. Calibrated to 1.4 against the
 * 2026-10-08 field test (suburban Perth, grid-ish streets with a river
 * crossing — straight-line 2.88 km ran ~4 km by road).
 */
export const ROAD_DISTANCE_FACTOR = 1.4

/**
 * Assumed MOVING average urban speed for the haversine fallback (km/h) —
 * what the car averages between the kerb and the destination street,
 * lights included. Used both for the pre-shift estimate (with
 * {@link TRIP_OVERHEAD_MINUTES} on top) and as the mid-journey ETA fallback
 * when no observed speed is available yet.
 */
export const URBAN_AVG_SPEED_KMH = 40

/**
 * Fixed per-trip overhead added to the pre-shift drive-time estimate
 * (minutes): getting going, the lights at either end, finding a park and
 * walking to the door. Door-to-door time is NOT proportional to distance —
 * a flat effective speed either undershoots short hops or overshoots long
 * ones — so the model is `overhead + roadKm / URBAN_AVG_SPEED_KMH`.
 *
 * Calibration (2026-10-08 field test): 2.88 km straight-line took 11.7 min
 * door to door. Old model (1.3 × 40 km/h, no overhead) → 5.6 min (−52%);
 * the member watched the ETA jump "5 min → 10 → 9" when observed speed took
 * over, and the leave-by fired after the provider had already left. New
 * model (5 min + 1.4 × 2.88 km / 40 km/h) → 11.0 min (−5%).
 */
export const TRIP_OVERHEAD_MINUTES = 5

/**
 * Arrival allowance folded into the mid-journey ETA (ms): the last few
 * hundred metres — slowing, parking, walking up — take real time that
 * `remaining ÷ speed` cannot see (2026-10-08 field test: 3.4 min from the
 * 500 m ring to the door). Decays with fix staleness like the rest of the ETA.
 */
export const ETA_ARRIVAL_ALLOWANCE_MS = 90_000

/**
 * Sanity ceiling for a computed one-way travel estimate (ms). Providers do not
 * drive to sessions for more than ~6 hours — anything above this means an
 * endpoint is wrong (classic case: a coarse network/IP geolocation fix placed
 * the provider on the other side of the continent, producing a "~97 hr travel"
 * push in the 2026-10-06 field test). Estimates above the ceiling are flagged
 * {@link TravelEstimate.unreliable}: callers must NOT assert the number in UI
 * or push copy, must not persist `leaveByAt`/`travelDurationMs` from it, and
 * should fall back to neutral "check your travel time" copy.
 */
export const MAX_SANE_TRAVEL_DURATION_MS = 6 * 60 * 60 * 1000

/** True when a travel duration is plausible enough to show/persist/schedule on. */
export function isSaneTravelDurationMs(ms: number | null | undefined): boolean {
  return ms != null && Number.isFinite(ms) && ms > 0 && ms <= MAX_SANE_TRAVEL_DURATION_MS
}

export type TravelEstimateSource = 'haversine' | 'routing'

export interface TravelEstimateOptions {
  avgSpeedKmh?: number
  roadFactor?: number
  earlyArrivalMinutes?: number
  baseBufferMinutes?: number
  /** Extra leave-by margin (default {@link LEAVE_BY_BUFFER_MINUTES}). */
  leaveByBufferMinutes?: number
  /** Per-trip overhead for the haversine model (default {@link TRIP_OVERHEAD_MINUTES}). */
  tripOverheadMinutes?: number
  /** Applied on top of haversine or routing duration when traffic data suggests delay. */
  trafficMultiplier?: number
  /** When set, skips haversine duration (from Amazon Location Service or similar). */
  routingDurationMs?: number | null
}

export interface TravelEstimate {
  travelDurationMs: number
  leaveByAt: number
  /** Early arrival + base buffer subtracted from session start (not part of drive time). */
  leaveByPaddingMs: number
  crowFliesKm: number
  estimatedRoadKm: number
  source: TravelEstimateSource
  /**
   * True when the computed duration exceeds {@link MAX_SANE_TRAVEL_DURATION_MS}
   * (an endpoint is almost certainly wrong). The raw numbers are still
   * returned for logging/diagnostics, but callers must not display, persist,
   * or schedule off them.
   */
  unreliable: boolean
}

/** @deprecated Pass `TravelEstimateOptions` instead — number is treated as `avgSpeedKmh`. */
export type LegacyAvgSpeedArg = number

function normalizeTravelOptions(
  options?: TravelEstimateOptions | LegacyAvgSpeedArg,
): TravelEstimateOptions {
  if (typeof options === 'number') return { avgSpeedKmh: options }
  return options ?? {}
}

/** Sum of early-arrival, base buffer and leave-by margin — applied only to leave-by (ms). */
export function getLeaveByPaddingMs(
  options?: Pick<
    TravelEstimateOptions,
    'earlyArrivalMinutes' | 'baseBufferMinutes' | 'leaveByBufferMinutes'
  >,
): number {
  const earlyMs = (options?.earlyArrivalMinutes ?? EARLY_ARRIVAL_MINUTES) * 60_000
  const bufferMs = (options?.baseBufferMinutes ?? BASE_TRAVEL_BUFFER_MINUTES) * 60_000
  const marginMs = (options?.leaveByBufferMinutes ?? LEAVE_BY_BUFFER_MINUTES) * 60_000
  return earlyMs + bufferMs + marginMs
}

/** Pre-shift / T-30 window — same lead time as the start reminder. */
export const PRE_SHIFT_WINDOW_MS = REMINDER_BEFORE_START_MS

/**
 * Pings are accepted for the whole en-route window — storage is bounded by
 * {@link trimJourneyPings}, not by rejecting fresh positions (rejecting them
 * is what starved the member ETA of GPS data in the 2026-10-06 field test).
 */
export function canAddJourneyPing(journey: SessionJourney | null | undefined): boolean {
  return journey?.status === 'en_route'
}

/**
 * Bound the stored ping list to {@link MAX_JOURNEY_PINGS}: always keep the
 * first ping (the journey origin — route progress is measured from it) and
 * the most recent pings after it; the older run is distance-compacted.
 *
 * @deprecated Prefer `appendJourneyPing` (journey-ping-policy.ts) when adding a ping — it also
 * stamps `cumulativeKm` so compaction stays lossless for the km sum.
 */
export function trimJourneyPings(pings: JourneyPing[]): JourneyPing[] {
  return compactJourneyPings(pings)
}

/** Most recent GPS-sourced ping (ignores `estimate` pings, which reuse stale coordinates). */
export function latestGpsPing(journey: SessionJourney | null | undefined): JourneyPing | null {
  if (!journey) return null
  for (let i = journey.pings.length - 1; i >= 0; i--) {
    const ping = journey.pings[i]
    if (ping.locationSource !== 'estimate') return ping
  }
  return null
}

/**
 * Straight-line progress from `origin` toward `destination` at `current` (0 = at origin, 1 = at destination).
 */
export function computeRouteProgress(
  origin: GeoPoint,
  destination: GeoPoint,
  current: GeoPoint,
): number {
  const totalKm = haversineKm(origin, destination)
  if (totalKm < 0.01) return 1
  const remainingKm = haversineKm(current, destination)
  return Math.max(0, Math.min(1, 1 - remainingKm / totalKm))
}

/** Distance moved from the first ping (metres). */
export function movementFromStartMeters(journey: SessionJourney, current: GeoPoint): number {
  const start = journey.pings[0]
  if (!start) return 0
  return haversineKm(start, current) * 1000
}

export interface JourneyStageInput {
  journey: SessionJourney | null | undefined
  destination: GeoPoint | null
  /** Latest GPS fix while en route (optional — used for progress/movement). */
  currentPosition?: GeoPoint | null
  /** Provider checked in — terminal stage 6 (arrived). */
  checkedIn?: boolean
  /** Pre-shift estimated one-way travel time (ms). Enables time-driven stages. */
  travelDurationMs?: number | null
  /** Override clock for tests. */
  now?: number
}

function clampJourneyStage(value: number): JourneyStage {
  if (value <= 1) return 1
  if (value >= JOURNEY_STAGE_COUNT) return JOURNEY_ARRIVED_STAGE
  return Math.round(value) as JourneyStage
}

/**
 * Resolve estimated one-way travel duration from session fields.
 * Falls back to leave-by offset from session start when only `leaveByAt` is stored.
 */
export function resolveTravelDurationMs(input: {
  start: number
  leaveByAt?: number | null
  travelDurationMs?: number | null
}): number | null {
  // An insane stored duration (bad geolocation endpoint — see
  // MAX_SANE_TRAVEL_DURATION_MS) must not drive stage/ETA math or UI copy.
  if (isSaneTravelDurationMs(input.travelDurationMs)) return input.travelDurationMs ?? null
  if (input.leaveByAt != null && input.leaveByAt < input.start) {
    const totalLeadMs = input.start - input.leaveByAt
    const paddingMs = getLeaveByPaddingMs()
    const inferredDriveMs = totalLeadMs - paddingMs
    // Legacy sessions stored leave-by without padding — inferred drive time would be too low.
    const resolved = inferredDriveMs > 60_000 ? inferredDriveMs : totalLeadMs
    return isSaneTravelDurationMs(resolved) ? resolved : null
  }
  return null
}

/** Elapsed ms since the provider started the journey. */
export function computeElapsedJourneyMs(
  journey: SessionJourney,
  now: number = Date.now(),
): number {
  const startedAt = journey.startedAt ?? journey.pings[0]?.timestamp
  if (startedAt == null) return 0
  return Math.max(0, now - startedAt)
}

/**
 * Time-driven stage while en route (fallback when GPS is absent/stale):
 * stage 2 at start, 3 at 45% of the estimated travel, 4 at 85%.
 * Stage 5 requires GPS geofence or check-in — time alone stops at 4.
 *
 * The thresholds mirror the route-progress ones ({@link JOURNEY_HALFWAY_PROGRESS},
 * {@link JOURNEY_NEARLY_THERE_PROGRESS}). They used to be T/5 and 2T/5, which
 * declared "Nearly there" at 40% of an already-optimistic estimate — the
 * 2026-10-06 field test showed stage 4/5 while the provider was 4.5 min out.
 */
export function computeTimeBasedJourneyStage(
  journey: SessionJourney,
  travelDurationMs: number,
  now: number = Date.now(),
): JourneyStage {
  if (journey.status !== 'en_route' || journey.pings.length === 0) return 1
  if (travelDurationMs <= 0) return 2

  const elapsed = computeElapsedJourneyMs(journey, now)
  const fraction = elapsed / travelDurationMs

  if (fraction >= JOURNEY_NEARLY_THERE_PROGRESS) return 4
  if (fraction >= JOURNEY_HALFWAY_PROGRESS) return 3
  return 2
}

/**
 * Minutes remaining until estimated arrival based on pre-shift travel duration.
 *
 * ⚠️ Pure countdown of an estimate — it never looks at where the provider
 * actually is. Use {@link computeJourneyEtaMinutes} for anything member-facing;
 * this remains only as the fallback when no GPS position exists.
 */
export function computeEtaMinutesRemaining(
  journey: SessionJourney,
  travelDurationMs: number,
  now: number = Date.now(),
): number {
  if (journey.status !== 'en_route') return 0
  const elapsed = computeElapsedJourneyMs(journey, now)
  const remainingMs = Math.max(0, travelDurationMs - elapsed)
  return Math.max(1, Math.round(remainingMs / 60_000))
}

/**
 * Observed average road speed across the GPS pings (km/h), when measurable.
 *
 * Gap-aware: only consecutive GPS-ping pairs separated by at most
 * {@link JOURNEY_GPS_FRESH_MS} (and moving at a plausible pair speed) count
 * toward the average. A first-to-latest average is poisoned by backgrounded
 * stretches — the 2026-10-07 field test had an 11-minute gap (app behind
 * Google Maps) that dragged the average to ~5 km/h and pushed the member a
 * "~90 min away" ETA for a 6 km trip. Teleport pairs (a stale cached fix
 * followed by a fresh one) are excluded by the pair-speed bound.
 */
function observedSpeedKmh(journey: SessionJourney): number | null {
  const gpsPings = journey.pings.filter((p) => p.locationSource !== 'estimate')
  if (gpsPings.length < 2) return null

  let coveredRoadKm = 0
  let elapsedMs = 0
  for (let i = 1; i < gpsPings.length; i++) {
    const from = gpsPings[i - 1]
    const to = gpsPings[i]
    const dtMs = to.timestamp - from.timestamp
    if (dtMs <= 0 || dtMs > JOURNEY_GPS_FRESH_MS) continue
    const pairRoadKm = haversineKm(from, to) * ROAD_DISTANCE_FACTOR
    const pairSpeed = pairRoadKm / (dtMs / 3_600_000)
    if (pairSpeed > MAX_OBSERVED_SPEED_KMH) continue
    coveredRoadKm += pairRoadKm
    elapsedMs += dtMs
  }

  if (elapsedMs < MIN_SPEED_OBSERVATION_MS) return null
  if (coveredRoadKm < MIN_SPEED_OBSERVATION_KM) return null
  const speed = coveredRoadKm / (elapsedMs / 3_600_000)
  if (speed < MIN_OBSERVED_SPEED_KMH || speed > MAX_OBSERVED_SPEED_KMH) return null
  return speed
}

/**
 * Shared remaining-distance view of an en-route journey — the SINGLE source
 * both the ETA text and the journey-strip car position derive from (the
 * 2026-10-07 field test showed "About 3 min away" while the car sat near the
 * start: the two surfaces ran on different inputs).
 *
 * Distances are road-adjusted ({@link ROAD_DISTANCE_FACTOR}). `null` when
 * there is no GPS anchor or destination.
 */
export interface JourneyRemaining {
  /** Road-adjusted km from the GPS anchor to the destination. */
  remainingRoadKm: number
  /** Road-adjusted km for the whole route (first ping → destination). */
  totalRoadKm: number
  /** Observed (gap-aware) or urban-fallback average speed (km/h). */
  speedKmh: number
  /** Timestamp of the GPS anchor the distances are measured from. */
  anchorTimestamp: number
  /** True when the anchor is inside the 500 m "very close by" geofence. */
  withinGeofence: boolean
  /**
   * True when the anchor is inside the tight arrival geofence
   * ({@link ARRIVAL_GEOFENCE_RADIUS_METERS}) — the ONLY condition under which
   * UI may say "Arriving now". Everywhere else the ETA is the computed
   * estimate floored at 1 min (2026-10-08 field test: the old hard-coded
   * "Under a minute away" inside 500 m showed for 3.5 minutes).
   */
  withinArrivalGeofence: boolean
}

export function computeJourneyRemaining(
  journey: SessionJourney | null | undefined,
  destination: GeoPoint | null | undefined,
  now: number = Date.now(),
  currentPosition?: GeoPoint | null,
): JourneyRemaining | null {
  if (!journey || journey.status !== 'en_route' || !destination) return null
  const origin = journey.pings[0]
  if (!origin) return null

  const gpsPing = latestGpsPing(journey)
  const anchorPoint = currentPosition ?? gpsPing
  if (!anchorPoint) return null
  const anchorTimestamp = currentPosition ? now : gpsPing!.timestamp

  const geofence = checkGeofence(anchorPoint, destination, DEFAULT_GEOFENCE_RADIUS_METERS)
  const arrivalGeofence = checkGeofence(anchorPoint, destination, ARRIVAL_GEOFENCE_RADIUS_METERS)
  const speedKmh = observedSpeedKmh(journey) ?? URBAN_AVG_SPEED_KMH

  return {
    remainingRoadKm: haversineKm(anchorPoint, destination) * ROAD_DISTANCE_FACTOR,
    totalRoadKm: haversineKm(origin, destination) * ROAD_DISTANCE_FACTOR,
    speedKmh,
    anchorTimestamp,
    withinGeofence: geofence.withinRadius,
    withinArrivalGeofence: arrivalGeofence.withinRadius,
  }
}

/**
 * Minutes (≥ 1) implied by a remaining-distance view: drive time at the
 * observed/fallback speed plus the arrival allowance, decayed by anchor
 * staleness.
 */
export function journeyRemainingToMinutes(remaining: JourneyRemaining, now: number): number {
  const remainingMs =
    (remaining.remainingRoadKm / remaining.speedKmh) * 3_600_000 + ETA_ARRIVAL_ALLOWANCE_MS
  // Assume continued progress since the fix was taken.
  const staleMs = Math.max(0, now - remaining.anchorTimestamp)
  return Math.max(1, Math.round((remainingMs - staleMs) / 60_000))
}

/**
 * GPS-anchored ETA (minutes): remaining road-adjusted distance from the most
 * recent real position to the session coordinates, over the observed average
 * speed (urban-average fallback), minus time already elapsed since that fix.
 * Always the computed figure floored at 1 — there is no geofence shortcut
 * (the 500 m "very close by" ring is still ~2 min of suburban driving plus
 * parking). "Arriving now" is a separate signal: {@link JourneyRemaining.withinArrivalGeofence}.
 * Returns `null` when there is no GPS position or destination to anchor to.
 */
export function computeGpsEtaMinutes(
  journey: SessionJourney | null | undefined,
  destination: GeoPoint | null | undefined,
  now: number = Date.now(),
  currentPosition?: GeoPoint | null,
): number | null {
  const remaining = computeJourneyRemaining(journey, destination, now, currentPosition)
  if (!remaining) return null
  return journeyRemainingToMinutes(remaining, now)
}

/**
 * True only when the latest real position sits inside the tight arrival
 * geofence — the single condition under which UI may say "Arriving now".
 */
export function isJourneyArrivingNow(
  journey: SessionJourney | null | undefined,
  destination: GeoPoint | null | undefined,
  now: number = Date.now(),
  currentPosition?: GeoPoint | null,
): boolean {
  return (
    computeJourneyRemaining(journey, destination, now, currentPosition)?.withinArrivalGeofence ??
    false
  )
}

export interface JourneyEtaInput {
  journey: SessionJourney | null | undefined
  /** Session coordinates — enables the GPS-anchored estimate. */
  destination?: GeoPoint | null
  /** Pre-shift travel estimate (ms) — time-countdown fallback only. */
  travelDurationMs?: number | null
  /** Live device fix (provider view) — treated as a fresh GPS anchor. */
  currentPosition?: GeoPoint | null
  now?: number
}

/**
 * Member/provider-facing ETA: GPS-anchored when any real position exists
 * (2026-10-06 field test: the time countdown said "7 min" while Google Maps
 * said 11 — positions must win), time-countdown only when GPS is absent,
 * last-ping snapshot as the final fallback.
 */
export function computeJourneyEtaMinutes(input: JourneyEtaInput): number | null {
  const { journey, destination, travelDurationMs, currentPosition, now = Date.now() } = input
  if (!journey || journey.status !== 'en_route') return null

  const gpsEta = computeGpsEtaMinutes(journey, destination ?? null, now, currentPosition)
  if (gpsEta != null) return gpsEta

  if (travelDurationMs != null && travelDurationMs > 0) {
    return computeEtaMinutesRemaining(journey, travelDurationMs, now)
  }

  const latest = journey.pings[journey.pings.length - 1]
  return latest?.etaMinutes ?? null
}

/**
 * Index (0–3) of the next elapsed fraction ping trigger, or -1 when all four post-start
 * boundaries have been crossed.
 */
export function nextJourneyPingFractionIndex(
  journey: SessionJourney,
  travelDurationMs: number,
  now: number = Date.now(),
): number {
  if (journey.status !== 'en_route' || travelDurationMs <= 0) return -1
  const elapsed = computeElapsedJourneyMs(journey, now)
  const fraction = elapsed / travelDurationMs
  for (let i = 0; i < JOURNEY_PING_ELAPSED_FRACTIONS.length; i++) {
    if (fraction < JOURNEY_PING_ELAPSED_FRACTIONS[i]) return i
  }
  return -1
}

/** How many post-start fraction boundaries have been crossed (0–4). */
export function crossedJourneyPingFractionCount(
  journey: SessionJourney,
  travelDurationMs: number,
  now: number = Date.now(),
): number {
  if (journey.status !== 'en_route' || travelDurationMs <= 0) return 0
  const elapsed = computeElapsedJourneyMs(journey, now)
  const fraction = elapsed / travelDurationMs
  let count = 0
  for (const threshold of JOURNEY_PING_ELAPSED_FRACTIONS) {
    if (fraction >= threshold) count++
  }
  return count
}

/** Whole minutes until leave-by; negative or zero means leave now. */
export function computeLeaveCountdownMinutes(leaveByAt: number, now: number = Date.now()): number {
  return Math.ceil((leaveByAt - now) / 60_000)
}

/** Countdown label for the departure card — "Leave now" or "Leave in N min". */
export function formatLeaveCountdown(leaveByAt: number, now: number = Date.now()): string {
  const minutes = computeLeaveCountdownMinutes(leaveByAt, now)
  if (minutes <= 0) return 'Leave now'
  if (minutes === 1) return 'Leave in 1 min'
  return `Leave in ${minutes} min`
}

/**
 * Derive the current journey stage (1–6) from journey state, elapsed time, GPS, and check-in.
 *
 * GPS/time evidence caps at stage 5 ("Very close by", 500 m geofence entry).
 * Stage 6 ("Arrived") requires `journey.status === 'arrived'` — set by the
 * server's accuracy-checked auto-arrival ({@link shouldAutoArrive}) or by
 * check-in. A single loose fix must never announce arrival; the tight-radius
 * + accuracy policy lives server-side so it fires exactly once.
 */
export function computeJourneyStage(input: JourneyStageInput): JourneyStage {
  const { journey, destination, currentPosition, checkedIn, travelDurationMs, now = Date.now() } =
    input

  if (checkedIn || journey?.status === 'arrived') return JOURNEY_ARRIVED_STAGE

  if (!journey || journey.status === 'none') return 1

  if (journey.status !== 'en_route') {
    const stored = journey.stage
    return stored != null && stored >= 1 ? clampJourneyStage(stored) : 1
  }

  if (journey.pings.length === 0) return 1

  // While en route, stored/evidence stages never exceed "Very close by".
  // (Legacy rows stored stage 5 on geofence entry when 5 still meant
  // "Arrived" — under the new labels the same value reads correctly.)
  const stored = journey.stage
  let stage: JourneyStage =
    stored != null && stored >= 2
      ? (Math.min(clampJourneyStage(stored), JOURNEY_VERY_CLOSE_STAGE) as JourneyStage)
      : 2
  if (stage === JOURNEY_VERY_CLOSE_STAGE) return stage

  // Fresh GPS is authoritative: stage derives from remaining distance to the
  // session coordinates. The time-based estimate only drives the stage when no
  // fresh position exists (2026-10-06 field test: the countdown against an
  // optimistic pre-shift estimate showed "Nearly there" with the provider
  // still ~4 km out).
  const gpsPing = latestGpsPing(journey)
  const freshGpsPoint =
    currentPosition ??
    (gpsPing && now - gpsPing.timestamp <= JOURNEY_GPS_FRESH_MS ? gpsPing : null)

  if (freshGpsPoint) {
    const moved = movementFromStartMeters(journey, freshGpsPoint)
    if (moved >= JOURNEY_MOVEMENT_THRESHOLD_METERS && stage < 2) stage = 2

    if (destination) {
      const progress = computeRouteProgress(journey.pings[0], destination, freshGpsPoint)
      if (progress >= JOURNEY_NEARLY_THERE_PROGRESS) stage = Math.max(stage, 4) as JourneyStage
      else if (progress >= JOURNEY_HALFWAY_PROGRESS) stage = Math.max(stage, 3) as JourneyStage

      const geofence = checkGeofence(freshGpsPoint, destination, DEFAULT_GEOFENCE_RADIUS_METERS)
      if (geofence.withinRadius) return JOURNEY_VERY_CLOSE_STAGE
    }
    return stage
  }

  if (travelDurationMs != null && travelDurationMs > 0) {
    stage = Math.max(stage, computeTimeBasedJourneyStage(journey, travelDurationMs, now)) as JourneyStage
  }

  return stage
}

/** Label for a journey stage index (1-based). */
export function journeyStageLabel(stage: JourneyStage): string {
  return JOURNEY_STAGE_LABELS[stage - 1] ?? `Stage ${stage}`
}

/** Progress bar fill ratio 0–1 for the car icon position. */
export function journeyStageProgressRatio(stage: JourneyStage): number {
  return (stage - 1) / (JOURNEY_STAGE_COUNT - 1)
}

/**
 * Estimate one-way drive time and leave-by for a face-to-face session.
 *
 * Drive time = {@link TRIP_OVERHEAD_MINUTES} + road-adjusted haversine ÷
 * {@link URBAN_AVG_SPEED_KMH}, unless `routingDurationMs` is supplied.
 * Leave-by = sessionStart − driveTime − earlyArrival − baseBuffer − leaveByBuffer.
 */
export function computeTravelEstimate(
  from: GeoPoint,
  to: GeoPoint,
  sessionStartMs: number,
  options?: TravelEstimateOptions | LegacyAvgSpeedArg,
): TravelEstimate {
  const opts = normalizeTravelOptions(options)
  const crowFliesKm = haversineKm(from, to)
  const roadFactor = opts.roadFactor ?? ROAD_DISTANCE_FACTOR
  const estimatedRoadKm = crowFliesKm * roadFactor
  const leaveByPaddingMs = getLeaveByPaddingMs(opts)

  let travelDurationMs: number
  let source: TravelEstimateSource

  if (opts.routingDurationMs != null && opts.routingDurationMs > 0) {
    travelDurationMs = opts.routingDurationMs
    source = 'routing'
  } else {
    const speedKmh = opts.avgSpeedKmh ?? URBAN_AVG_SPEED_KMH
    const overheadMinutes = opts.tripOverheadMinutes ?? TRIP_OVERHEAD_MINUTES
    travelDurationMs =
      (estimatedRoadKm / speedKmh) * 60 * 60 * 1000 + overheadMinutes * 60_000
    source = 'haversine'
  }

  const trafficMultiplier = opts.trafficMultiplier ?? 1
  if (trafficMultiplier > 1) {
    travelDurationMs = Math.round(travelDurationMs * trafficMultiplier)
  }

  travelDurationMs = Math.max(60_000, Math.round(travelDurationMs))
  const leaveByAt = sessionStartMs - travelDurationMs - leaveByPaddingMs

  return {
    travelDurationMs,
    leaveByAt,
    leaveByPaddingMs,
    crowFliesKm,
    estimatedRoadKm,
    source,
    unreliable: !isSaneTravelDurationMs(travelDurationMs),
  }
}

/** One-way drive duration (ms) — excludes early-arrival and base buffers. */
export function computeTravelDurationMs(
  from: GeoPoint,
  to: GeoPoint,
  options?: TravelEstimateOptions | LegacyAvgSpeedArg,
): number {
  return computeTravelEstimate(from, to, 0, options).travelDurationMs
}

/** When the provider should leave to arrive early with buffer time before session start. */
export function computeLeaveByAt(
  from: GeoPoint,
  to: GeoPoint,
  sessionStartMs: number,
  options?: TravelEstimateOptions | LegacyAvgSpeedArg,
): number {
  return computeTravelEstimate(from, to, sessionStartMs, options).leaveByAt
}

/** Human-readable travel duration for UI and push copy. */
export function formatTravelDurationMs(ms: number): string {
  const totalMinutes = Math.max(1, Math.round(ms / 60_000))
  if (totalMinutes < 60) return `${totalMinutes} min`
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`
}

/** Google Maps deep link for turn-by-turn navigation to a destination. */
export function googleMapsDirectionsUrl(destination: GeoPoint): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`
}

/** True during the T-30 window before the scheduled session start. */
export function isInPreShiftWindow(now: number, sessionStart: number): boolean {
  const windowStart = sessionStart - PRE_SHIFT_WINDOW_MS
  return now >= windowStart && now < sessionStart
}

/** Member map shows the provider pin only while en route. */
export function shouldShowProviderPing(journey: SessionJourney | null | undefined): boolean {
  return journey?.status === 'en_route' && journey.pings.length > 0
}

/** Map free-text `serviceType` to a checklist discipline (defaults to support work). */
export function disciplineFromServiceType(serviceType: string): ChecklistDiscipline {
  const normalized = serviceType.trim().toLowerCase()
  if (normalized.includes('occupational') || /\bot\b/.test(normalized)) return 'ot'
  if (normalized.includes('physio')) return 'physio'
  if (normalized.includes('speech')) return 'speech'
  if (normalized.includes('therapy assistant') || /\bta\b/.test(normalized)) return 'ta'
  return 'support_work'
}

export function emptyJourney(): SessionJourney {
  return { status: 'none', pings: [], startedAt: null, arrivedAt: null, stage: 1 }
}
